import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  Badge,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import axios from "../../../config/axiosConfig";
import MuiAlert from "@mui/material/Alert";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";
import { useAuth } from "../../../context/AuthContext";

// Estado 1-2 (Emitido/Detalle) de una OT de Mantenimiento (tipo_ot=2): espejo de
// index.php + guarda.php del sistema viejo. A diferencia de Adición/Devolución, acá
// es un solo paso: se marca qué equipo Activo del cliente entra a mantenimiento (con
// una observación inicial opcional por fila) y "Enviar a Mantenimiento" hace todo de
// una — marca los equipos, guarda historial y avanza la OT a "Asignado" (3) — no hay
// un botón "Finalizar" aparte en el header como en Adición/Devolución.
// AVL/Scada no entra acá: ese Mantenimiento ya lo cubre el módulo Requerimiento
// (modules/requerimiento/reqMantenimientoModel.js), igual que ya excluye Adición.
// Mismo componente para Mantenimiento Traspaso (tipo_ot=8, exclusivo de Cochabamba):
// con esMantenimientoTraspaso=true, "Enviar a Mantenimiento" además imprime la Nota de
// Traspaso — a partir de ahí la OT ya no tiene más pasos (ver puedeDiagnosticar en
// OrdenTrabajoPaso.jsx).
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };
const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
};
// Input nativo (Box component="input") en vez de TextField variant="standard" para
// la celda de observación sin label — mismo criterio que AsignarEquiposVariosOT.
const inputCompactoSx = {
  fontFamily: "inherit",
  fontSize: 10.5,
  padding: "2px 2px",
  border: "none",
  borderBottom: "1px solid",
  borderColor: "divider",
  outline: "none",
  bgcolor: "transparent",
  color: "text.primary",
  "&:focus": { borderColor: "primary.main" },
  "&:disabled": { color: "text.disabled" },
};

const TIPOS_TKR = ["Radio", "Bateria"];
const TIPOS_OTROS = ["Varios", "Chip"];
const TIPO_LABEL = { Radio: "Radios", Bateria: "Baterías", Varios: "Equipos", Chip: "Chips" };
// El endpoint de relacionados (/api/ot-mantenimiento/relacionados) devuelve `familia` en
// mayúsculas (VARIOS, CHIP), igual que el resto del backend — distinto de TIPO_LABEL,
// que usa la capitalización de los toggles del catálogo (Varios, Chip, ...).
const FAMILIA_LABEL_RELACIONADO = { VARIOS: "Equipo", CHIP: "Chip" };
const PAGE_SIZE = 15;

const endpointDeTipo = (tipo) => {
  if (tipo === "Radio") return "/api/equipos-radio";
  if (tipo === "Bateria") return "/api/equipos-bateria";
  if (tipo === "Chip") return "/api/equipos-chip";
  return "/api/equipos-varios";
};

const SeleccionarMantenimientoOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, esTKR, esMantenimientoTraspaso, esMantenimientoVendidos, onEnviado }) => {
  // Mantenimiento Equipos Vendidos también lista los equipos Vendido Activo del cliente.
  const estadosListado = esMantenimientoVendidos ? "Activo,Vendido Activo" : "Activo";
  const { user } = useAuth();
  const tipos = esTKR ? TIPOS_TKR : TIPOS_OTROS;
  const [tipo, setTipo] = useState(tipos[0]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [loadingEquipos, setLoadingEquipos] = useState(true);
  const [error, setError] = useState("");

  // { [key]: { tipoEquipo, identificador, observacion } }
  const [seleccion, setSeleccion] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [errores, setErrores] = useState([]);
  const [equipoDialog, setEquipoDialog] = useState(null);
  const [confirmarOpen, setConfirmarOpen] = useState(false);
  // Baterías/chips que se incluirán automáticamente por estar relacionados a un
  // equipo Varios seleccionado (ver propagación en enviarAMantenimientoTx) — se
  // muestran en el diálogo de confirmación para que no sea una sorpresa en la Nota.
  const [relacionados, setRelacionados] = useState([]);
  const [loadingRelacionados, setLoadingRelacionados] = useState(false);
  // Mismo dato que `relacionados`, pero para mostrar en el listado de equipos (por
  // identificador) antes de seleccionar nada, no solo en el diálogo de confirmación.
  const [relacionadosPorIdentificador, setRelacionadosPorIdentificador] = useState({});
  const [loadingRelacionadosListado, setLoadingRelacionadosListado] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cargarPagina = (nextPage = page, buscarTexto = buscarAplicado) => {
    setLoadingEquipos(true);
    setError("");
    axios
      .get(endpointDeTipo(tipo), {
        params: {
          estado: estadosListado,
          cod_servicio: cod_servicio_cliente,
          // Solo equipo de la sucursal del usuario logueado — mismo criterio que
          // DevolucionEquiposOT: no tiene sentido mandar a mantenimiento equipo
          // que está físicamente en otra ciudad. user puede venir null en el
          // primer render tras un refresh (useAuth() todavía rehidratando).
          ciudad: user?.sucursal || undefined,
          buscar: buscarTexto,
          page: nextPage,
          pageSize: PAGE_SIZE,
        },
      })
      .then((res) => {
        if (tipo === "Chip") {
          const { rows = [], total: totalChips = 0 } = res.data || {};
          setItems(rows.map((r) => ({ key: `chip-${r.ID}`, tipoEquipo: "Chip", identificador: r.numero, modelo: r.modelo, ciudad: r.ciudad, estado: r.estado })));
          setTotal(Number(totalChips));
          setPage(nextPage);
        } else {
          const data = res.data || {};
          setItems((data.items || []).map((r) => ({ key: `${tipo.toLowerCase()}-${r.ID}`, tipoEquipo: tipo, identificador: r.serie, modelo: r.modelo, ciudad: r.ciudad, estado: r.estado })));
          setTotal(Number(data.total || 0));
          setPage(Number(data.page || nextPage));
        }
      })
      .catch((err) => {
        console.error("Error al cargar equipos activos del cliente", err);
        setError("No se pudo cargar el equipo activo del cliente.");
      })
      .finally(() => setLoadingEquipos(false));
  };

  useEffect(() => {
    setBuscar("");
    setBuscarAplicado("");
    cargarPagina(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, cod_servicio_cliente]);

  // Cantidad de equipo Activo por CADA tipo (no solo el seleccionado en el
  // ToggleButtonGroup) — mismo criterio que DevolucionEquiposOT.jsx: badge con
  // el total real, pidiendo solo 1 fila (page/pageSize:1) por tipo, en paralelo.
  const [conteos, setConteos] = useState({});

  const cargarConteos = () => {
    Promise.all(
      tipos.map((t) =>
        axios
          .get(endpointDeTipo(t), {
            params: {
              estado: estadosListado,
              cod_servicio: cod_servicio_cliente,
              ciudad: user?.sucursal || undefined,
              page: 1,
              pageSize: 1,
            },
          })
          .then((res) => [t, Number(res.data?.total) || 0])
          .catch(() => [t, null])
      )
    ).then((pares) => setConteos(Object.fromEntries(pares)));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargarConteos, [cod_servicio_cliente, esTKR, user?.sucursal]);

  // Solo "Varios" propaga a baterías/chips relacionados al enviar a Mantenimiento (ver
  // enviarAMantenimientoTx) — se cargan los relacionados de lo que está visible en la
  // página actual para mostrarlos en la tabla, antes de que el usuario marque nada.
  useEffect(() => {
    if (tipo !== "Varios" || !items.length) {
      setRelacionadosPorIdentificador({});
      return;
    }
    let activo = true;
    setLoadingRelacionadosListado(true);
    Promise.all(
      items.map((it) =>
        axios.get(`/api/ot-mantenimiento/relacionados/${it.identificador}`)
          .then((res) => [it.identificador, res.data || []])
          .catch(() => [it.identificador, []])
      )
    ).then((pares) => {
      if (!activo) return;
      setRelacionadosPorIdentificador(Object.fromEntries(pares));
    }).finally(() => {
      if (activo) setLoadingRelacionadosListado(false);
    });
    return () => { activo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, tipo]);

  useEffect(() => {
    setTipo((esTKR ? TIPOS_TKR : TIPOS_OTROS)[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esTKR]);

  const handleBuscar = () => {
    setBuscarAplicado(buscar.trim());
    cargarPagina(1, buscar.trim());
  };

  const toggleEquipo = (row) => {
    setSeleccion((prev) => {
      if (prev[row.key]) {
        const { [row.key]: _quitado, ...resto } = prev;
        return resto;
      }
      return { ...prev, [row.key]: { tipoEquipo: row.tipoEquipo, identificador: row.identificador, modelo: row.modelo, observacion: "" } };
    });
  };

  const setCampoEquipo = (key, valor) => {
    setSeleccion((prev) => ({ ...prev, [key]: { ...prev[key], observacion: valor } }));
  };

  const seleccionadosEntries = Object.entries(seleccion);
  const seleccionados = Object.values(seleccion);

  const abrirConfirmar = async () => {
    setConfirmarOpen(true);
    const varios = seleccionados.filter((s) => s.tipoEquipo === "Varios");
    if (!varios.length) {
      setRelacionados([]);
      return;
    }
    setLoadingRelacionados(true);
    try {
      const resultados = await Promise.all(
        varios.map((s) =>
          axios.get(`/api/ot-mantenimiento/relacionados/${s.identificador}`).then((res) => res.data || [])
        )
      );
      const vistos = new Set();
      const combinados = resultados.flat().filter((r) => {
        const key = `${r.familia}-${r.identificador}`;
        if (vistos.has(key)) return false;
        vistos.add(key);
        return true;
      });
      setRelacionados(combinados);
    } catch {
      setRelacionados([]);
    } finally {
      setLoadingRelacionados(false);
    }
  };

  const handleEnviar = async () => {
    if (!seleccionados.length) return;
    setEnviando(true);
    setErrores([]);

    // Mantenimiento Traspaso (Cochabamba) imprime la Nota apenas se envía el equipo —
    // se abre la pestaña ya (en blanco) antes de cualquier await, en el mismo gesto de
    // click, para que el navegador no la bloquee como popup — igual que
    // DesactivarVendidosOT.jsx.
    const pdfWindow = esMantenimientoTraspaso ? window.open("", "_blank") : null;
    if (pdfWindow) {
      pdfWindow.opener = null;
      pdfWindow.document.title = "Generando Nota de Traspaso...";
      pdfWindow.document.body.innerHTML =
        '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Enviando a Mantenimiento y generando la Nota de Traspaso...</div>';
    }

    try {
      const payload = {
        cod_cliente,
        seleccion: seleccionados.map((s) => ({
          familia: s.tipoEquipo.toUpperCase(),
          identificador: s.identificador,
          observacion: s.observacion || "",
        })),
      };
      const { data } = await axios.post(`/api/ot-mantenimiento/enviar/${cod_ot}`, payload);
      if (onEnviado) onEnviado(data);
      setConfirmarOpen(false);
      cargarConteos();

      if (pdfWindow) {
        try {
          const { data: pdfData } = await axios.get(`/api/ot-solicitudes/${cod_ot}/nota-mantenimiento-traspaso.pdf`, { responseType: "blob" });
          const pdfUrl = URL.createObjectURL(new Blob([pdfData], { type: "application/pdf" }));
          pdfWindow.location.replace(pdfUrl);
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
        } catch {
          pdfWindow.close();
        }
      }
    } catch (err) {
      if (pdfWindow) pdfWindow.close();
      setErrores([err?.response?.data?.error || "No se pudo enviar a Mantenimiento."]);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Equipos Activos del Cliente
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          color="primary"
          value={tipo}
          onChange={(e, nuevo) => nuevo && setTipo(nuevo)}
          sx={{ height: 30 }}
        >
          {tipos.map((t) => (
            <ToggleButton key={t} value={t} sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
              <Badge
                badgeContent={conteos[t] ?? 0}
                color="error"
                max={999}
                showZero
                sx={{ "& .MuiBadge-badge": { right: -8, top: 2 } }}
              >
                {TIPO_LABEL[t]}
              </Badge>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 1, rowGap: 1 }}>
        <TextField
          size="small"
          label="Buscar"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleBuscar();
          }}
          sx={{ minWidth: 160, ...controlSx }}
        />
        <IconButton size="small" onClick={handleBuscar} title="Buscar">
          <SearchIcon fontSize="small" />
        </IconButton>
        <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
        <Chip size="small" color="warning" variant="outlined" label={`Seleccionados: ${seleccionados.length}`} sx={{ height: 20, fontSize: 11 }} />
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 0.5, maxHeight: 360, borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
              <TableCell sx={headCellSx}>Identificador</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
              <TableCell sx={headCellSx}>Ciudad</TableCell>
              {esMantenimientoVendidos && <TableCell sx={headCellSx}>Estado</TableCell>}
              {tipo === "Varios" && <TableCell sx={headCellSx}>Relacionados</TableCell>}
              <TableCell sx={headCellSx}>Observación</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingEquipos ? (
              <TableRow>
                <TableCell colSpan={(tipo === "Varios" ? 6 : 5) + (esMantenimientoVendidos ? 1 : 0)} align="center" sx={{ py: 2 }}>
                  <CircularProgress size={18} />
                </TableCell>
              </TableRow>
            ) : !items.length ? (
              <TableRow>
                <TableCell colSpan={(tipo === "Varios" ? 6 : 5) + (esMantenimientoVendidos ? 1 : 0)}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Este cliente no tiene {TIPO_LABEL[tipo].toLowerCase()} activos.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => {
                const checked = Boolean(seleccion[row.key]);
                const relacionadosFila = relacionadosPorIdentificador[row.identificador];
                return (
                  <TableRow key={row.key} hover selected={checked}>
                    <TableCell>
                      <Checkbox size="small" checked={checked} onChange={() => toggleEquipo(row)} sx={{ p: 0 }} />
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        onClick={() => setEquipoDialog({ tipo: row.tipoEquipo.toLowerCase(), identificador: row.identificador })}
                        sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                      >
                        {row.identificador}
                      </Box>
                    </TableCell>
                    <TableCell>{row.modelo}</TableCell>
                    <TableCell>{row.ciudad}</TableCell>
                    {esMantenimientoVendidos && (
                      <TableCell>{row.estado === "Vendido Activo" ? <Chip size="small" color="warning" label="Vendido activo" /> : "Activo"}</TableCell>
                    )}
                    {tipo === "Varios" && (
                      <TableCell>
                        {loadingRelacionadosListado && !relacionadosFila ? (
                          <CircularProgress size={12} />
                        ) : !relacionadosFila?.length ? (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        ) : (
                          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                            {relacionadosFila.map((r) => (
                              <Chip
                                key={`${r.familia}-${r.identificador}`}
                                size="small"
                                variant="outlined"
                                label={`${FAMILIA_LABEL_RELACIONADO[r.familia] || r.familia} ${r.identificador}`}
                                sx={{ height: 18, fontSize: 9.5 }}
                              />
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Box
                        component="input"
                        disabled={!checked}
                        value={seleccion[row.key]?.observacion || ""}
                        onChange={(e) => setCampoEquipo(row.key, e.target.value)}
                        sx={{ ...inputCompactoSx, width: "100%" }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Página {total ? page : 0} / {total ? totalPages : 0}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<NavigateBeforeIcon />}
          disabled={loadingEquipos || page <= 1}
          onClick={() => cargarPagina(page - 1)}
          sx={{ minHeight: 26, height: 26, fontSize: 11 }}
        >
          Anterior
        </Button>
        <Button
          size="small"
          variant="outlined"
          endIcon={<NavigateNextIcon />}
          disabled={loadingEquipos || page >= totalPages}
          onClick={() => cargarPagina(page + 1)}
          sx={{ minHeight: 26, height: 26, fontSize: 11 }}
        >
          Siguiente
        </Button>
      </Stack>

      {!!errores.length && (
        <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>
          {errores.map((msg, i) => (
            <div key={i}>{msg}</div>
          ))}
        </MuiAlert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={!seleccionados.length || enviando}
          onClick={abrirConfirmar}
          sx={{ minHeight: 28, height: 28, fontSize: 11.5, px: 2 }}
        >
          Enviar a Mantenimiento
        </Button>
      </Box>

      <Dialog open={confirmarOpen} onClose={() => !enviando && setConfirmarOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirmar Envío a Mantenimiento</DialogTitle>
        <DialogContent>
          {esMantenimientoTraspaso && (
            <MuiAlert severity="info" variant="outlined" sx={{ mb: 1.5, fontSize: 11.5 }}>
              Se imprimirá la Nota de Traspaso y esta OT no tendrá más pasos para tu sucursal.
            </MuiAlert>
          )}
          {!!errores.length && (
            <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>
              {errores.map((msg, i) => (<div key={i}>{msg}</div>))}
            </MuiAlert>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Tipo</TableCell>
                  <TableCell sx={headCellSx}>Identificador</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={headCellSx}>Observación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {seleccionadosEntries.map(([key, datos]) => (
                  <TableRow key={key} hover>
                    <TableCell>{TIPO_LABEL[datos.tipoEquipo] || datos.tipoEquipo}</TableCell>
                    <TableCell>{datos.identificador}</TableCell>
                    <TableCell>{datos.modelo}</TableCell>
                    <TableCell>{datos.observacion || "-"}</TableCell>
                  </TableRow>
                ))}
                {relacionados.map((r) => (
                  <TableRow key={`${r.familia}-${r.identificador}`} hover sx={{ bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)") }}>
                    <TableCell>{FAMILIA_LABEL_RELACIONADO[r.familia] || r.familia}</TableCell>
                    <TableCell>{r.identificador}</TableCell>
                    <TableCell>{r.modelo}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label="Automático" sx={{ height: 18, fontSize: 10 }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {loadingRelacionados && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={16} />
            </Box>
          )}
          {!loadingRelacionados && !!relacionados.length && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Las filas marcadas "Automático" se incluyen porque están relacionadas al equipo seleccionado (mismo criterio que ya usa Mantenimiento).
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmarOpen(false)} disabled={enviando}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={!seleccionados.length || enviando}
            onClick={handleEnviar}
          >
            {enviando ? <CircularProgress size={16} color="inherit" /> : "Confirmar Envío"}
          </Button>
        </DialogActions>
      </Dialog>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default SeleccionarMantenimientoOT;
